import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { hasRequiredVertical } from '../helpers';
import { UserLoginService } from '../service/user-login.service';
import { AuthableVertical, UserProfile } from '../types';
import { CanActivateResponse } from '../types/guard';

/**
 * Redirects to home when the user has no access to any of the route's `verticals` data. Admins always pass.
 */
@Injectable({
  providedIn: 'root',
})
export class VerticalGuard implements CanActivate, CanActivateChild {
  private userService = inject(UserLoginService);
  private router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    const requiredVerticals: AuthableVertical[] | undefined = route.data.verticals;
    const user = this.userService.currentProfile();
    if (!user) {
      return this.userService.currentProfileLoaded$.pipe(
        first(),
        map((loadedUser) => this.evaluate(requiredVerticals, loadedUser)),
      );
    }

    return this.evaluate(requiredVerticals, user);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    return this.canActivate(childRoute);
  }

  evaluate(requiredVerticals: AuthableVertical[] | undefined, user: UserProfile): CanActivateResponse {
    if (hasRequiredVertical(requiredVerticals, user.business_verticals ?? [], this.userService.currentUserIsAdmin())) {
      return true;
    }

    return this.router.createUrlTree(['home']);
  }
}
